// src/hooks/usePedidos.js
import { useState, useEffect } from "react";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import { notificarPedidoConfirmado, avisarTransportista, confirmarEntrega } from "../utils/whatsapp";
import { emailPedidoConfirmado, emailEntregaConfirmada } from "../utils/notificaciones";

const COL = "pedidos";

export function usePedidos() {
  const [pedidos,  setPedidos]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const q = query(collection(db, COL), orderBy("creadoEn", "desc"));
    return onSnapshot(q, snap => {
      setPedidos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  // ── CREAR PEDIDO + notificaciones automáticas ──────────────────
  const crearPedido = async (datos, cliente, transportista) => {
    // Leer los últimos 10 pedidos para encontrar el número máximo real
    const snap = await getDocs(query(collection(db, COL), orderBy("creadoEn", "desc"), limit(10)));
    let maxNum = 0;
    snap.docs.forEach(d => {
      const m = (d.data().numero || "").match(/(\d+)$/);
      if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
    });
    const numero = `PED-${String(maxNum + 1).padStart(4, "0")}`;
    const pedido = { ...datos, numero, estado: "Solicitud recibida", creadoEn: serverTimestamp() };
    try {
      const ref = await addDoc(collection(db, COL), pedido);
      toast.success(`✅ Pedido #${numero} creado`);

      // Disparar notificaciones automáticas
      if (cliente?.telefono) {
        await notificarPedidoConfirmado(cliente, { ...pedido, numero });
        toast("📱 WhatsApp enviado al cliente", { icon: "✅" });
      }
      if (cliente?.email) {
        await emailPedidoConfirmado(cliente, { ...pedido, numero });
      }
      if (transportista?.telefono) {
        await avisarTransportista(transportista, { ...pedido, numero }, cliente);
        toast("🚚 WhatsApp enviado al transportista", { icon: "✅" });
      }
      return ref.id;
    } catch (e) {
      toast.error("Error al crear pedido");
      console.error(e);
    }
  };

  // ── ACTUALIZAR ESTADO + lógica por estado ─────────────────────
  const actualizarEstado = async (pedidoId, nuevoEstado, pedido, cliente, transportista) => {
    try {
      await updateDoc(doc(db, COL, pedidoId), {
        estado: nuevoEstado,
        actualizadoEn: serverTimestamp(),
        ...(nuevoEstado === "Entregado" ? { fechaEntregaReal: new Date().toISOString() } : {}),
      });
      toast.success(`Estado actualizado: ${nuevoEstado}`);

      // Aviso transportista cuando pedido está listo para despacho
      if (nuevoEstado === "Preparando" && transportista?.telefono) {
        await avisarTransportista(transportista, pedido, cliente);
        toast("🚚 Transportista notificado", { icon: "📱" });
      }

      // Confirmación de entrega al cliente
      if (nuevoEstado === "Entregado") {
        if (cliente?.telefono) await confirmarEntrega(cliente, pedido);
        if (cliente?.email)    await emailEntregaConfirmada(cliente, pedido);
        toast("✅ Cliente notificado de entrega", { icon: "📱" });
      }
    } catch (e) {
      toast.error("Error al actualizar estado");
      console.error(e);
    }
  };

  const actualizarPedido = async (id, datos) => {
    await updateDoc(doc(db, COL, id), { ...datos, actualizadoEn: serverTimestamp() });
    toast.success("Pedido actualizado");
  };

  const eliminarPedido = async (id) => {
    await deleteDoc(doc(db, COL, id));
    toast.success("Pedido eliminado");
  };

  // KPIs calculados
  const kpis = {
    total:     pedidos.length,
    activos:   pedidos.filter(p => !["Entregado","Cancelado"].includes(p.estado)).length,
    hoy:       pedidos.filter(p => { const d=new Date(); const hoyL=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; return p.fechaEntrega === hoyL; }).length,
    enTransito: pedidos.filter(p => p.estado === "En tránsito").length,
    valorTotal: pedidos.filter(p => p.estado !== "Cancelado").reduce((s,p) => s + (Number(p.valor)||0), 0),
    cobrarPendiente: pedidos.filter(p => p.estado === "Entregado" && !p.pagado).reduce((s,p) => s + (Number(p.valor)||0), 0),
  };

  return { pedidos, loading, kpis, crearPedido, actualizarEstado, actualizarPedido, eliminarPedido };
}
